import React, { createContext, useContext, useReducer, ReactNode } from 'react';

export interface WizardState {
  // Resume Selection
  selectedResumeId: string;
  uploadedFile: File | null;
  pastedResumeText: string;
  processedResumeData: string;
  
  // Job Information
  tailoringSource: 'job-description' | 'job-position' | 'institution' | 'assessment-questions' | 'revisions' | '';
  jobDescription: string;
  jobPositionTitle: string;
  jobPositionIndustry: string;
  selectedJobId: string;
  
  // Document Selection
  selectedDocumentTypes: string[];
  
  // Tailoring Preferences
  sectionsToTailor: string[];
  selectedTemplateMain: string;
  quantificationLevel: string;
  writingStyle: string;
  selectedPrompts: Record<string, string>; // document_type -> prompt_id
  
  // Additional Instructions
  additionalInstructionsText: string;
  additionalInstructionsDocuments: File[];
  
  // Processing State
  isProcessing: boolean;
  currentStep: number;
  
  // Results
  tailoredResumeContent: string;
  coverLetterContent: string;
  linkedinContent: string;
  thankYouLetterContent: string;
  outreachEmailContent: string;
  executiveBioContent: string;
  analysisData: any;
}

type WizardAction =
  | { type: 'SET_RESUME_DATA'; payload: { selectedResumeId?: string; uploadedFile?: File | null; pastedResumeText?: string; processedResumeData?: string } }
  | { type: 'SET_JOB_DATA'; payload: { tailoringSource?: 'job-description' | 'job-position' | 'institution' | 'assessment-questions' | 'revisions' | ''; jobDescription?: string; jobPositionTitle?: string; jobPositionIndustry?: string; selectedJobId?: string } }
  | { type: 'SET_DOCUMENT_TYPES'; payload: { selectedDocumentTypes: string[] } }
  | { type: 'SET_PREFERENCES'; payload: { sectionsToTailor?: string[]; selectedTemplateMain?: string; quantificationLevel?: string; writingStyle?: string; selectedPrompts?: Record<string, string> } }
  | { type: 'SET_ADDITIONAL_INSTRUCTIONS'; payload: { additionalInstructionsText?: string; additionalInstructionsDocuments?: File[] } }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | { type: 'SET_RESULTS'; payload: { tailoredResumeContent?: string; coverLetterContent?: string; linkedinContent?: string; thankYouLetterContent?: string; outreachEmailContent?: string; executiveBioContent?: string; analysisData?: any } }
  | { type: 'RESET_WIZARD' };

const initialState: WizardState = {
  selectedResumeId: '',
  uploadedFile: null,
  pastedResumeText: '',
  processedResumeData: '',
  tailoringSource: '' as '' | 'job-description' | 'job-position' | 'institution' | 'assessment-questions' | 'revisions',
  jobDescription: '',
  jobPositionTitle: '',
  jobPositionIndustry: '',
  selectedJobId: '',
  selectedDocumentTypes: ['resume'],
  sectionsToTailor: ['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'],
  selectedTemplateMain: '',
  quantificationLevel: '',
  writingStyle: 'chronological',
  selectedPrompts: {},
  additionalInstructionsText: '',
  additionalInstructionsDocuments: [],
  isProcessing: false,
  currentStep: 1,
  tailoredResumeContent: '',
  coverLetterContent: '',
  linkedinContent: '',
  thankYouLetterContent: '',
  outreachEmailContent: '',
  executiveBioContent: '',
  analysisData: null,
};

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_RESUME_DATA':
      return { ...state, ...action.payload };
    case 'SET_JOB_DATA':
      return { ...state, ...action.payload };
    case 'SET_DOCUMENT_TYPES':
      return { ...state, ...action.payload };
    case 'SET_PREFERENCES':
      return { ...state, ...action.payload };
    case 'SET_ADDITIONAL_INSTRUCTIONS':
      return { ...state, ...action.payload };
    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.payload };
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_RESULTS':
      return { ...state, ...action.payload };
    case 'RESET_WIZARD':
      return initialState;
    default:
      return state;
  }
}

const ResumeTailoringContext = createContext<{
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
} | null>(null);

export function ResumeTailoringProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(wizardReducer, initialState);

  return (
    <ResumeTailoringContext.Provider value={{ state, dispatch }}>
      {children}
    </ResumeTailoringContext.Provider>
  );
}

export function useResumeTailoring() {
  const context = useContext(ResumeTailoringContext);
  if (!context) {
    throw new Error('useResumeTailoring must be used within a ResumeTailoringProvider');
  }
  return context;
}